import { PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import { useCreateInvestor, useInvestors, type InvestorQuery } from '../api/hooks';
import { MAX_VIDEO_SECONDS } from '../components/investor/documentRules';
import PendingDocumentPicker from '../components/investor/PendingDocumentPicker';
import { useAuth } from '../context/AuthContext';
import type { DocumentType, InvestorListItem, WhatsAppStatus } from '../types';
import { apiDate, money } from '../utils/format';
import { paymentMethodOptions } from '../utils/paymentMethods';

const CNIC_PATTERN = /^\d{5}-\d{7}-\d$/;
const MOBILE_PATTERN = /^03\d{2}-\d{7}$/;

export default function InvestorsPage() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [query, setQuery] = useState<InvestorQuery>({ page: 1, pageSize: 20, sortBy: 'created_at', sortDir: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isFetching, error } = useInvestors(query);

  return (
    <>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Investors
        </Typography.Title>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
            Add investor
          </Button>
        )}
      </Space>

      {error && <Alert type="error" showIcon message={errorMessage(error)} style={{ marginBottom: 16 }} />}

      <Card>
        <Input.Search
          allowClear
          placeholder="Search by name, mobile, CNIC or code"
          style={{ maxWidth: 360, marginBottom: 16 }}
          onSearch={(value) => setQuery((q) => ({ ...q, search: value || undefined, page: 1 }))}
        />

        <Table<InvestorListItem>
          rowKey="id"
          loading={isFetching}
          dataSource={data?.items ?? []}
          scroll={{ x: 'max-content' }}
          onRow={(row) => ({ onClick: () => navigate(`/investors/${row.id}`), style: { cursor: 'pointer' } })}
          pagination={{
            current: query.page,
            pageSize: query.pageSize,
            total: data?.totalCount ?? 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} investors`,
          }}
          onChange={(pagination, _filters, sorter) => {
            const s = sorter as SorterResult<InvestorListItem>;
            setQuery((q) => ({
              ...q,
              page: pagination.current ?? 1,
              pageSize: pagination.pageSize ?? 20,
              sortBy: (s.field as string) ? toColumn(s.field as string) : q.sortBy,
              sortDir: s.order === 'ascend' ? 'asc' : 'desc',
            }));
          }}
          columns={[
            { title: 'Code', dataIndex: 'investorCode', sorter: true },
            { title: 'Name', dataIndex: 'fullName', sorter: true },
            { title: 'Mobile', dataIndex: 'mobile', render: (v: string | null) => v ?? '—' },
            { title: 'CNIC', dataIndex: 'cnic', render: (v: string | null) => v ?? '—' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (value: string) => <Tag color={value === 'Active' ? 'green' : 'default'}>{value}</Tag>,
            },
            { title: 'Total capital', dataIndex: 'totalCapital', align: 'right', sorter: true, render: money },
            {
              title: 'Outstanding profit',
              dataIndex: 'outstandingProfit',
              align: 'right',
              render: (value: number) => (
                <span style={{ color: value > 0 ? '#cf1322' : undefined }}>{money(value)}</span>
              ),
            },
            {
              title: 'Actions',
              render: (_, row) => (
                <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); navigate(`/investors/${row.id}`); }}>
                  View
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <CreateInvestorModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}

/** Maps the antd column dataIndex to the SQL column the API sorts on. */
function toColumn(field: string) {
  const map: Record<string, string> = {
    investorCode: 'investor_code',
    fullName: 'full_name',
    totalCapital: 'total_capital',
    createdAt: 'created_at',
  };
  return map[field] ?? 'created_at';
}

/**
 * Tells the admin whether the confirmation actually reached WhatsApp, and what to do when it
 * didn't. The password is never part of that message, so it always has to be handed over manually.
 */
function WhatsAppNotice({ sent, status }: { sent: boolean; status: WhatsAppStatus }) {
  const notice: Record<WhatsAppStatus, { type: 'success' | 'info' | 'warning' | 'error'; text: string }> = {
    Sent: { type: 'success', text: 'WhatsApp confirmation sent to the investor.' },
    Disabled: { type: 'info', text: 'WhatsApp is switched off, so no message was sent.' },
    NotConfigured: {
      type: 'warning',
      text: 'WhatsApp is enabled but not configured — check PhoneNumberId and AccessToken.',
    },
    NoMobile: { type: 'warning', text: 'No mobile number on file, so no WhatsApp message was sent.' },
    InvalidMobile: {
      type: 'warning',
      text: 'The mobile number could not be read as a valid number — no message sent.',
    },
    Failed: { type: 'error', text: 'WhatsApp message failed to send. See the server log for details.' },
  };

  const { type, text } = notice[status] ?? notice.Failed;

  return (
    <Alert
      type={sent ? 'success' : type}
      showIcon
      style={{ marginTop: 12 }}
      message={text}
      description="Give the investor their password in person — it is deliberately never sent over WhatsApp."
    />
  );
}

function CreateInvestorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const createInvestor = useCreateInvestor();
  const [photo, setPhoto] = useState<File | null>(null);
  const [cnicImage, setCnicImage] = useState<File | null>(null);
  const [agreementVideo, setAgreementVideo] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const resetAll = () => {
    form.resetFields();
    setPhoto(null);
    setCnicImage(null);
    setAgreementVideo(null);
  };

  /**
   * Documents can only be attached once the investor row exists, so they are uploaded after
   * creation. A failed upload must not read as a failed creation — the account is already real,
   * so problems are reported per-file and the admin can retry from the profile tab.
   */
  const uploadPending = async (investorId: number): Promise<string[]> => {
    const pending: Array<[DocumentType, File | null]> = [
      ['photo', photo],
      ['cnic-image', cnicImage],
      ['agreement-video', agreementVideo],
    ];

    const failed: string[] = [];
    for (const [type, file] of pending) {
      if (!file) continue;
      try {
        const body = new FormData();
        body.append('file', file);
        await api.post(`/investors/${investorId}/documents/${type}`, body, {
          headers: { 'Content-Type': undefined },
        });
      } catch {
        failed.push(type);
      }
    }
    return failed;
  };

  const onFinish = async (values: Record<string, unknown>) => {
    // Documents are not Form.Items — they are files held in state until the investor row exists —
    // so antd's `required` rules cannot see them. Checked here instead, before anything is created.
    const missingDocs = [
      !photo && 'profile photo',
      !cnicImage && 'CNIC image',
      !agreementVideo && 'agreement video',
    ].filter(Boolean);

    if (missingDocs.length > 0) {
      message.error(`Attach the ${missingDocs.join(', ')} before creating the account.`);
      return;
    }

    try {
      const result = await createInvestor.mutateAsync({
        ...values,
        initialInvestmentDate: apiDate(values.initialInvestmentDate as dayjs.Dayjs | undefined),
      });

      setUploading(true);
      const failedUploads = await uploadPending(result.investor.id);
      setUploading(false);
      if (failedUploads.length > 0) {
        message.warning(
          `Investor created, but these files did not upload: ${failedUploads.join(', ')}. Add them from the profile tab.`,
        );
      }

      // The generated password is shown once and never returned again.
      Modal.success({
        title: 'Investor created',
        width: 480,
        content: (
          <div>
            <p>
              Code / username: <strong>{result.username}</strong>
            </p>
            <p>
              Temporary password: <strong>{result.generatedPassword}</strong>
            </p>
            <p style={{ color: '#cf1322' }}>Save this password now — it will not be shown again.</p>
            <WhatsAppNotice sent={result.whatsAppSent} status={result.whatsAppStatus} />
          </div>
        ),
      });
      resetAll();
      onClose();
    } catch (err) {
      setUploading(false);
      message.error(errorMessage(err));
    }
  };

  return (
    <Modal
      title="Add investor"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={createInvestor.isPending || uploading}
      okText="Create"
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
        <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="fatherName" label="Father's name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="mobile"
          label="Mobile"
          rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Format: 03XX-XXXXXXX' }]}
        >
          <Input placeholder="0300-1234567" />
        </Form.Item>
        {/* CNIC can never be edited once the account exists, so it is worth getting right here. */}
        <Form.Item
          name="cnic"
          label="CNIC"
          rules={[{ required: true }, { pattern: CNIC_PATTERN, message: 'Format: XXXXX-XXXXXXX-X' }]}
          help="Cannot be changed after the account is created."
        >
          <Input placeholder="35202-1234567-1" />
        </Form.Item>
        <Form.Item name="address" label="Address" rules={[{ required: true }]} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item name="paymentMethod" label="Payment method" rules={[{ required: true }]}>
          <Select placeholder="Select how money moves to and from this investor" options={paymentMethodOptions} />
        </Form.Item>
        <Form.Item name="bankAccount" label="Account / wallet number" rules={[{ required: true }]}>
          <Input placeholder="Account title, IBAN or wallet number" />
        </Form.Item>

        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          Documents
        </Divider>
        <PendingDocumentPicker
          type="photo"
          label="Profile photo"
          hint="JPG, PNG or WebP · max 5 MB"
          file={photo}
          onChange={setPhoto}
        />
        <PendingDocumentPicker
          type="cnic-image"
          label="CNIC image"
          hint="JPG, PNG or WebP · max 5 MB"
          file={cnicImage}
          onChange={setCnicImage}
        />
        <PendingDocumentPicker
          type="agreement-video"
          label="Agreement video"
          hint={`MP4 or WebM · max ${MAX_VIDEO_SECONDS} seconds · 25 MB`}
          file={agreementVideo}
          onChange={setAgreementVideo}
        />

        <Divider orientation="left" plain>
          Initial investment
        </Divider>
        <Form.Item name="initialAmount" label="Amount" rules={[{ required: true }]} style={{ marginTop: 8 }}>
          <InputNumber min={0.01} style={{ width: '100%' }} />
        </Form.Item>
        {/* Optional even when an amount is entered: staff often take the money in before the rate
            is agreed. The tranche is then booked at 0% and accrues nothing until it is set. */}
        <Form.Item
          name="initialProfitRate"
          label="Monthly profit rate (%)"
          help="Optional. Leave blank if the rate is not agreed yet — no profit accrues until it is set."
        >
          <InputNumber min={0.01} max={99.99} step={0.01} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="initialInvestmentDate" label="Investment date" rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
